import { SoccerStages } from '@adflow/types';

import {
  MockPreviewValues,
  getMockScoreValue,
  getPreviewDate,
} from '../components/previewConstants';
import {
  FULL_TIME_RESULT_PREVIEW_DATA_POINT_NAMES,
  type SoccerPreviewData,
  type SoccerPreviewValue,
} from './resolvePreviewData';

type FullTimeResultPreviewName = (typeof FULL_TIME_RESULT_PREVIEW_DATA_POINT_NAMES)[number];

// Design-mode soccer placeholders should explain the mapping semantics
// instead of looking like fixture-derived content.
export const SOCCER_DESIGN_TEXT_PLACEHOLDERS = {
  TEAM_HOME_NAME: 'Home Team',
  TEAM_AWAY_NAME: 'Away Team',
  TEAM_HOME_ABBREVIATION: 'HOM',
  TEAM_AWAY_ABBREVIATION: 'AWA',
  EVENT_ROUND_NAME: 'Round Name',
  MOMENT_GOAL_SCORER_NAME: 'Scorer Name',
  POST_EVENT_OUTCOME: 'Home Win',
  POST_WINNER_TEAM_NAME: 'Winner Team',
} as const;

const buildFullTimeResultDesignData = (isSupported: boolean): SoccerPreviewData =>
  FULL_TIME_RESULT_PREVIEW_DATA_POINT_NAMES.reduce<SoccerPreviewData>((data, key) => {
    data[key] = isSupported
      ? (MockPreviewValues[key as FullTimeResultPreviewName] as SoccerPreviewValue)
      : '';
    return data;
  }, {});

const getRequiredMockScoreValue = (key: string, sourceType?: string): string =>
  getMockScoreValue(key, sourceType) ?? '';

const buildCommonData = (): SoccerPreviewData => ({
  TEAM_HOME_NAME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.TEAM_HOME_NAME,
  TEAM_AWAY_NAME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.TEAM_AWAY_NAME,
  TEAM_HOME_ABBREVIATION: SOCCER_DESIGN_TEXT_PLACEHOLDERS.TEAM_HOME_ABBREVIATION,
  TEAM_AWAY_ABBREVIATION: SOCCER_DESIGN_TEXT_PLACEHOLDERS.TEAM_AWAY_ABBREVIATION,
  TEAM_HOME_COMPETITOR_ID: MockPreviewValues.TEAM_HOME_COMPETITOR_ID,
  TEAM_AWAY_COMPETITOR_ID: MockPreviewValues.TEAM_AWAY_COMPETITOR_ID,
  EVENT_ROUND_NAME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.EVENT_ROUND_NAME,
});

export const resolveDesignData = (stage: SoccerStages): SoccerPreviewData => {
  const commonData = buildCommonData();

  switch (stage) {
    case SoccerStages.PRE_EVENT: {
      const previewDate = getPreviewDate();

      return {
        ...commonData,
        DAY: previewDate,
        DATE: previewDate,
        MONTH: previewDate,
        TIME: previewDate,
        COUNTDOWN: previewDate,
        ...buildFullTimeResultDesignData(true),
      };
    }
    case SoccerStages.LIVE:
      return {
        ...commonData,
        LIVE_TEAM_HOME_SCORE: getRequiredMockScoreValue('LIVE_TEAM_HOME_SCORE'),
        LIVE_TEAM_AWAY_SCORE: getRequiredMockScoreValue('LIVE_TEAM_AWAY_SCORE'),
        LIVE_FULL_SCORE: getRequiredMockScoreValue('LIVE_FULL_SCORE'),
        ...buildFullTimeResultDesignData(true),
      };
    case SoccerStages.LIVE_GOAL:
      return {
        ...commonData,
        LIVE_TEAM_HOME_SCORE: getRequiredMockScoreValue('LIVE_TEAM_HOME_SCORE'),
        LIVE_TEAM_AWAY_SCORE: getRequiredMockScoreValue('LIVE_TEAM_AWAY_SCORE'),
        LIVE_FULL_SCORE: getRequiredMockScoreValue('LIVE_FULL_SCORE'),
        MOMENT_GOAL_SCORER_NAME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.MOMENT_GOAL_SCORER_NAME,
        MOMENT_GOAL_MATCH_TIME: MockPreviewValues.MOMENT_GOAL_MATCH_TIME,
        MOMENT_GOAL_TEAM_COMPETITOR_ID: MockPreviewValues.MOMENT_GOAL_TEAM_COMPETITOR_ID,
        ...buildFullTimeResultDesignData(false),
      };
    case SoccerStages.POST_EVENT:
      return {
        ...commonData,
        POST_TEAM_HOME_SCORE: getRequiredMockScoreValue('POST_TEAM_HOME_SCORE', 'SOCCER_POST'),
        POST_TEAM_AWAY_SCORE: getRequiredMockScoreValue('POST_TEAM_AWAY_SCORE', 'SOCCER_POST'),
        POST_FULL_SCORE: getRequiredMockScoreValue('POST_FULL_SCORE', 'SOCCER_POST'),
        POST_EVENT_OUTCOME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.POST_EVENT_OUTCOME,
        ...buildFullTimeResultDesignData(false),
      };
    case SoccerStages.POST_WINNER:
      return {
        ...commonData,
        POST_TEAM_HOME_SCORE: getRequiredMockScoreValue(
          'POST_TEAM_HOME_SCORE',
          'SOCCER_POST_WINNER',
        ),
        POST_TEAM_AWAY_SCORE: getRequiredMockScoreValue(
          'POST_TEAM_AWAY_SCORE',
          'SOCCER_POST_WINNER',
        ),
        POST_FULL_SCORE: getRequiredMockScoreValue('POST_FULL_SCORE', 'SOCCER_POST_WINNER'),
        POST_EVENT_OUTCOME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.POST_EVENT_OUTCOME,
        POST_WINNER_TEAM_NAME: SOCCER_DESIGN_TEXT_PLACEHOLDERS.POST_WINNER_TEAM_NAME,
        POST_WINNER_TEAM_COMPETITOR_ID: MockPreviewValues.POST_WINNER_TEAM_COMPETITOR_ID,
        ...buildFullTimeResultDesignData(false),
      };
  }
};
